import React, { useState, useEffect } from 'react';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import CardBanner from '../../assets/images/card-banner.png';
import CardAvatar from '../../assets/images/card-avatar.png';
import { CustomColors } from '../../theme';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Asset } from '../../types/assets';
import { useNavigate } from 'react-router-dom';
import { formatCurrency, truncateText } from '../../utils/helpers';
import moment from 'moment';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import { IconButton } from '@mui/material';

const cardStyles: React.CSSProperties = {
  position: 'relative',
};

const avatarStyles = {
  width: '60px',
  height: '50px',
  objectFit: 'cover',
};

const avatarContainer: React.CSSProperties = {
  position: 'absolute',
  bottom: '-20px',
  left: '10px',
  padding: '10px',
  width: '80px',
  height: '70px',
  zIndex: 1,
  backgroundColor: 'white',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '10px',
};

const fundContainer: React.CSSProperties = {
  position: 'absolute',
  top: '10px',
  right: '10px',
  zIndex: 1,
};

const listItemStyles: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0px',
  margin: '0px',
  height: '40px',
};

interface Props {
  asset: Asset;
}

type UserChoices = string[];

export default function AssetCard({ asset }: Props) {
  const navigate = useNavigate();

  const initialIsStarred =
    localStorage.getItem('userChoices')?.includes(asset.id) || false;
  const [isStarred, setIsStarred] = useState<boolean>(initialIsStarred);

  const handleStarClick = () => {
    const userChoices = localStorage.getItem('userChoices') || '[]';
    const parsedUserChoices = JSON.parse(userChoices) as UserChoices;

    const updatedUserChoices = parsedUserChoices.includes(asset.id)
      ? parsedUserChoices.filter((id) => id !== asset.id)
      : [...parsedUserChoices, asset.id];

    localStorage.setItem('userChoices', JSON.stringify(updatedUserChoices));
    setIsStarred(!isStarred);
  };

  useEffect(() => {
    const starred = localStorage.getItem('userChoices')?.includes(asset.id);
    if (starred !== isStarred) {
      setIsStarred(starred || false);
    }
  }, [asset.id, isStarred]);

  return (
    <Card
      sx={{
        borderRadius: '10px',
        marginY: '40px',
        cursor: 'pointer',
        minHeight: '600px',
      }}
      onClick={() => navigate(`/asset/${asset?.id}`)}
    >
      <div style={cardStyles}>
        <CardMedia
          sx={{ height: 180 }}
          image={
            asset?.company?.cover && asset?.company?.cover.length > 0
              ? asset?.company?.cover[0]
              : CardBanner
          }
          title="card"
        />
        <div style={avatarContainer}>
          <Avatar
            variant="square"
            alt="Logo"
            src={asset?.company?.logo ? asset?.company?.logo : CardAvatar}
            sx={avatarStyles}
          />
        </div>
        <div style={fundContainer} onClick={(e) => e.stopPropagation()}>
          <IconButton onClick={handleStarClick} style={{ background: '#fff' }}>
            {isStarred ? <StarIcon color="secondary" /> : <StarBorderIcon />}
          </IconButton>
        </div>
      </div>

      <CardContent sx={{ marginTop: '30px' }}>
        <Typography
          gutterBottom
          variant="h6"
          style={{ fontSize: '20px' }}
          component="div"
        >
          {asset?.company?.companyName || ''}
        </Typography>
        <Typography variant="body2" style={{ height: '90px' }}>
          {asset?.company?.companySummary}
        </Typography>
        <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
          <ListItem sx={listItemStyles}>
            <ListItemText primary="Round" />
            <p style={{ textAlign: 'end', fontWeight: '600' }}>
              {asset?.investmentInfo?.round
                ? formatCurrency(asset?.investmentInfo?.round)
                : '0'}
            </p>
          </ListItem>
          <Divider component="li" />
          <ListItem sx={listItemStyles}>
            <ListItemText primary="Raise Close" />
            <p style={{ textAlign: 'end', fontWeight: '600' }}>
              {asset?.investmentInfo?.raiseClose
                ? moment(asset?.investmentInfo?.raiseClose).format('DD-MM-YYYY')
                : '-'}
            </p>
          </ListItem>
          <Divider component="li" />
          <ListItem sx={listItemStyles}>
            <ListItemText primary="Min Investment" />
            <p style={{ textAlign: 'end', fontWeight: '600' }}>
              {' '}
              {asset?.investmentInfo?.minimumInvestment
                ? formatCurrency(asset?.investmentInfo?.minimumInvestment)
                : '0'}
            </p>
          </ListItem>
          <Divider component="li" />
          <ListItem sx={listItemStyles}>
            <ListItemText primary="Raise Amount" />
            <p style={{ textAlign: 'end', fontWeight: '600' }}>
              {asset?.investmentInfo?.raiseTarget
                ? formatCurrency(asset?.investmentInfo?.raiseTarget)
                : '0'}
            </p>
          </ListItem>
          <Divider component="li" />
        </List>
      </CardContent>
      <CardActions style={{ display: 'flex', justifyContent: 'space-between' }}>
        {asset?.company?.tags && asset?.company?.tags.length > 0 && (
          <Chip
            label={asset?.company?.tags[0]}
            style={{
              backgroundColor: CustomColors.grey900,
              color: CustomColors.white,
            }}
          />
        )}

        <p
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <LocationOnIcon />{' '}
          <span style={{ marginRight: '20px', marginLeft: '10px' }}>
            {asset?.company?.location}
          </span>
        </p>
      </CardActions>
    </Card>
  );
}
